'use client';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';

import { CreatePaymentFlowInput, useCreatePaymentFlow } from '@/lib/api/paymentFlows';
import {
  PaymentFlowForm,
  PaymentFlowFormData,
} from '@/components/form/payment-flow/payment-flow-form';
import { routingForSubmit } from '@/components/form/payment-flow/processor-routing';
import { Button } from '@/components/ui/button';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

/**
 * Org-level page for creating a new payment flow
 */
const CreatePaymentFlowPageInner = () => {
  const { orgRoutes, navigate } = useOrgNavigate();
  const createPaymentFlow = useCreatePaymentFlow();
  const { t } = useTranslation(['paymentFlows', 'common']);

  const handleSubmit = async (data: PaymentFlowFormData) => {
    const createInput = {
      data: {
        name: data.name,
        strategy: 'cascade' as const,
        fallbackMode: data.fallbackMode,
        maxFallbackRetries: data.maxFallbackRetries,
        threeDsEnabled: data.threeDsEnabled,
        stickyProcessorEnabled: data.stickyProcessorEnabled,
        pickProcessorStrategy: data.pickProcessorStrategy,
        processorConfigs: data.processorConfigs.map((config) => ({
          ...config,
          routing: routingForSubmit(config.routing),
        })),
        fallbackProcessorConfigs: data.fallbackProcessorConfigs,
      },
    } as CreatePaymentFlowInput;
    const result = await createPaymentFlow.mutateAsync(createInput);
    navigate(orgRoutes.PAYMENT_FLOW_DETAILS(result.id));
  };

  const handleCancel = () => {
    navigate(orgRoutes.PAYMENT_FLOWS());
  };

  return (
    <PaymentFlowForm
      Header={({ isPending, onCancel }) => (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={onCancel} disabled={isPending}>
              <ArrowLeft className="size-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold sm:text-3xl">
                {t('paymentFlows:create.title')}
              </h1>
              <p className="text-muted-foreground text-sm">
                {t('paymentFlows:create.subtitle')}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isPending}
            >
              {t('common:cancel')}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? t('common:creating')
                : t('paymentFlows:create.button')}
            </Button>
          </div>
        </div>
      )}
      isPending={createPaymentFlow.isPending}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );
};

export const CreatePaymentFlowPage = withRequiredPermission(CreatePaymentFlowPageInner, PERMISSIONS.PROCESSORS_MANAGE);
export default CreatePaymentFlowPage;


